import { useStorageFiles } from "@features/storage/use-storage.hook";
import { EMPTY_LIST } from "@shared/config/constants";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Input } from "@shared/ui/input";
import { Skeleton } from "@shared/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/ui/table";
import {
	File as FileIcon,
	FileText,
	Filter,
	Image as ImageIcon,
	Music,
	Search,
	Trash2,
	Upload,
	Video,
} from "lucide-react";
import { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const typeIcon = {
	image: ImageIcon,
	video: Video,
	audio: Music,
	doc: FileText,
} as const;

const typeTone: Record<string, string> = {
	image: "bg-primary/15 text-primary",
	video: "bg-purple-500/15 text-purple-500",
	audio: "bg-yellow-500/15 text-yellow-500",
	doc: "bg-blue-500/15 text-blue-500",
};

function getFileType(name: string): keyof typeof typeIcon {
	const ext = name.split(".").pop()?.toLowerCase() ?? "";
	if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "image";
	if (["mp4", "mov", "avi", "webm"].includes(ext)) return "video";
	if (["mp3", "wav", "ogg", "flac"].includes(ext)) return "audio";
	return "doc";
}

function formatSize(bytes?: number): string {
	if (!bytes) return "—";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type Props = {
	bucket: string;
};

export function FilesTable({ bucket }: Props) {
	const intl = useIntl();
	const { files, isPending, uploadFile, deleteFile } = useStorageFiles(bucket);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) await uploadFile(file);
		e.target.value = "";
	};

	return (
		<Card>
			<CardHeader className="space-y-3">
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="font-mono text-base">{bucket}/</CardTitle>
						<CardDescription>
							<FormattedMessage
								id="page.storage.files.count"
								defaultMessage="{count} files"
								values={{ count: files.length }}
							/>
						</CardDescription>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm">
							<Filter className="mr-1 h-4 w-4" />
							<FormattedMessage id="page.storage.files.filter" defaultMessage="Filter" />
						</Button>
						<Button size="sm" onClick={() => inputRef.current?.click()}>
							<Upload className="mr-1 h-4 w-4" />
							<FormattedMessage id="page.storage.files.upload" defaultMessage="Upload" />
						</Button>
						<input ref={inputRef} type="file" className="hidden" onChange={handleFileChange} />
					</div>
				</div>
				<div className="relative">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder={intl.formatMessage({
							id: "page.storage.files.searchPlaceholder",
							defaultMessage: "Search files…",
						})}
						className="pl-9"
					/>
				</div>
			</CardHeader>
			<CardContent>
				{isPending ? (
					<div className="space-y-2">
						{EMPTY_LIST.map((listItem) => (
							<Skeleton key={listItem} className="h-12 w-full" />
						))}
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-9">
									<Checkbox />
								</TableHead>
								<TableHead>
									<FormattedMessage id="page.storage.files.column.name" defaultMessage="Name" />
								</TableHead>
								<TableHead className="text-right">
									<FormattedMessage id="page.storage.files.column.size" defaultMessage="Size" />
								</TableHead>
								<TableHead>
									<FormattedMessage
										id="page.storage.files.column.modified"
										defaultMessage="Modified"
									/>
								</TableHead>
								<TableHead className="w-9" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{files.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
										<FormattedMessage
											id="page.storage.files.empty"
											defaultMessage="No files in this bucket"
										/>
									</TableCell>
								</TableRow>
							) : (
								files.map((f) => {
									const type = getFileType(f.name);
									const Icon = typeIcon[type] ?? FileIcon;
									return (
										<TableRow key={f.name}>
											<TableCell>
												<Checkbox />
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-3">
													<span
														className={`flex h-8 w-8 items-center justify-center rounded-md ${typeTone[type]}`}
													>
														<Icon className="h-4 w-4" />
													</span>
													<span className="font-medium">{f.name}</span>
												</div>
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatSize(f.metadata?.size as number)}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}
											</TableCell>
											<TableCell>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-muted-foreground hover:text-destructive"
													onClick={() => deleteFile([f.name])}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
